import _ from "lodash/fp";
import { getItemsByName } from "../../BulkProcesses/BatchDataUpload/bdu";

const getSelectedProgram = (selectedProgram) => (userAccessiblePrograms) => {
  let program = "";

  if (selectedProgram) {
    program = userAccessiblePrograms.find(
      ({ label }) => label === selectedProgram,
    );
  }

  return program;
};

const getSelectedItemNames = (getValues) => {
  return (
    Array.isArray(getValues("itemNames"))
      ? getValues("itemNames").join(",")
      : getValues("itemNames") || ""
  )
    .split(/[\n,]/)
    .map((name) => name.trim())
    .filter(Boolean);
};

const getSelectedItemFromDB = (CHUNKSIZE) => (client) => async (itemNames) => {
  const itemNameChunks = _.chunk(CHUNKSIZE)(itemNames);
  const getItemsPromises = itemNameChunks.map((itemName) =>
    getItemsByName(client)({ itemList: itemName }),
  );

  const promiseRespose = await Promise.allSettled(getItemsPromises);

  return promiseRespose.map(({ value }) => value).flat(Infinity);
};

const getItemsWithoutDBData = (itemNames) => (selectedItemsFromDB) => {
  let itemsWithoutDBData = [];

  if (itemNames.length > 0) {
    const itemNamesFromDB = selectedItemsFromDB.map((item) => item.name);
    const itemsNotInDB = itemNames.filter(
      (itemName) => !itemNamesFromDB.includes(itemName),
    );

    if (itemsNotInDB.length > 0) {
      itemsWithoutDBData = itemsNotInDB.map((item) => {
        return {
          id: item.concat("-id"),
          name: item,
          statusMessage: "Not Found",
          type: "item",
          __typename: "SimpleObject",
        };
      });
    }

    return itemsWithoutDBData;
  }
};

const getItemsWithPermission =
  (selectedItemsFromDB) => (userProgramsWithPermission) => {
    return selectedItemsFromDB.filter((item) =>
      userProgramsWithPermission.has(item.program),
    );
  };

const getItemsWithPermissionOfProgram = (itemsWithPermission) => (program) => {
  let itemsWithPermissionOfProgram = [];
  if (itemsWithPermission.length > 0) {
    itemsWithPermissionOfProgram = itemsWithPermission
      .filter((item) => item.program === program.value)
      .map((item) => item.name);
  }

  return itemsWithPermissionOfProgram;
};

const getItemsWithPermissionDifferentProgram =
  (itemsWithPermission) => (program) => {
    let itemsWithPermissionOfDifferentProgram = [];
    if (itemsWithPermission.length > 0) {
      itemsWithPermissionOfDifferentProgram = itemsWithPermission
        .filter((item) => item.program !== program.value)
        .map((item) => {
          return {
            id: item.id,
            name: item.name,
            statusMessage: "Item from different program",
            type: "item",
            __typename: "SimpleObject",
          };
        });
    }

    return itemsWithPermissionOfDifferentProgram;
  };

const getItemsWithoutPermission =
  (selectedItemsFromDB) => (userProgramsWithPermission) => {
    let itemsWithoutPermission = [];

    if (selectedItemsFromDB.length > 0) {
      const rawItems = selectedItemsFromDB.filter(
        (item) => !userProgramsWithPermission.has(item.program),
      );

      if (rawItems.length > 0) {
        itemsWithoutPermission = rawItems.map((item) => {
          return {
            id: item.id,
            name: item.name,
            statusMessage: "User does not have program permission",
            type: "item",
            __typename: "SimpleObject",
          };
        });
      }
    }

    return itemsWithoutPermission;
  };

const updateItemsInDB =
  (CHUNKSIZE) =>
  (client) =>
  (itemsWithPermission) =>
  async (selectedProgram) => {
    const filteredItemNameChunks = _.chunk(CHUNKSIZE)(itemsWithPermission);
    const bucPromises = filteredItemNameChunks.map((itemName) =>
      requestBUC(client)(itemName)(selectedProgram.value),
    );

    const bucResponses = await Promise.allSettled(bucPromises);
    let resultsUpdate = bucResponses
      .filter(({ status }) => status === "fulfilled")
      .map(({ value }) => value)
      .flat(Infinity);

    return resultsUpdate;
  };

const requestBUC = (client) => (itemNames) => async (program) => {
  return await client
    .mutation(
      batchUpdateContentMutation,
      { itemNames, program },
      { requestPolicy: "network-only" },
    )
    .toPromise()
    .then((response) => {
      return response.data?.batchUpdateContent;
    })
    .catch((error) => {
      console.error("ERROR", error);
      return [];
    });
};

const batchUpdateContentMutation = `
  mutation batchUpdateContent($itemNames: [String!]!, $program: ID) {
    batchUpdateContent(itemNames: $itemNames, program: $program) {
      developmentState
      id
      name
      statusMessage
      type
      versionID
    }
  }
`;

const getAlertMessage = (itemsWithoutPermission) => {
  let alertMessage = {
    message: `Batch Update Content Completed!`,
    options: { variant: "success" },
  };

  if (itemsWithoutPermission.length > 0) {
    alertMessage = {
      message: `Batch Update Content Completed with errors!`,
      options: { variant: "error" },
    };
  }

  return alertMessage;
};

export {
  getSelectedProgram,
  getSelectedItemNames,
  getSelectedItemFromDB,
  getItemsWithPermission,
  getItemsWithPermissionOfProgram,
  getItemsWithoutPermission,
  getItemsWithPermissionDifferentProgram,
  updateItemsInDB,
  getAlertMessage,
  getItemsWithoutDBData,
};
